import type { CSSProperties, ReactNode } from "react";

interface Props {
  /** 지름(px). HERIT: 생성 120 · 인쇄 140 */
  size?: number;
  /** 0–1 진행률. 없으면 무한 회전 */
  progress?: number;
  /** 호 색 — 기본 잉크, accent 는 그린(인쇄) */
  accent?: boolean;
  /** 중앙에 올릴 아이콘. 없으면 그린 점(r14) */
  children?: ReactNode;
  className?: string;
}

/**
 * 링 스피너 (HERIT 04a/05b) — 얇은 트랙(line 1px) 위에 진행 호(잉크 2px · 그린 2.4px), 중앙 점 또는 아이콘.
 * progress 가 없으면 CSS 로 돈다(.spinner--spin). 클래스명 ring 은 Tailwind 유틸과 충돌해 피한다.
 */
export function RingSpinner({ size = 120, progress, accent = false, children, className }: Props) {
  const r = size * 0.433; // 120 → 52, 140 → 60.6 (레퍼런스 r52/r60)
  const c = 2 * Math.PI * r;
  const p = progress === undefined ? 0.34 : Math.min(1, Math.max(0, progress));
  const cls = ["spinner", progress === undefined ? "spinner--spin" : "", className ?? ""].filter(Boolean).join(" ");
  // 지름은 CSS 변수로 — 화면이 .spinner 에 --ring-size 를 덮어쓰면(인쇄 화면이 컨테이너 높이에 맞춰 줄임) svg 도 100% 로 따라간다
  return (
    <div className={cls} style={{ "--ring-size": `${size}px` } as CSSProperties} role={progress === undefined ? "status" : undefined}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-line)" strokeWidth="1" />
        <circle
          className="spinner__arc"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={accent ? "var(--color-green)" : "var(--color-ink)"}
          strokeWidth={accent ? 2.4 : 2}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - p)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        {children ? null : <circle cx={size / 2} cy={size / 2} r={size * 0.117} fill="var(--color-green)" />}
      </svg>
      {children ? <span className="spinner__icon">{children}</span> : null}
    </div>
  );
}
